'use client'

/**
 * Message Bubble Component
 *
 * Renders individual chat messages with markdown support.
 */

import { cn } from '@/lib/utils'
import { User, Bot, UserCheck } from 'lucide-react'
import type { MessageBubbleProps } from './types'

/**
 * Parse and render markdown content
 * Supports: images, links, bold, italic, lists, code blocks
 */
function renderMarkdown(content: string): React.ReactNode {
  // Split by code blocks first
  const codeBlockRegex = /```(\w*)\n?([\s\S]*?)```/g
  const parts: React.ReactNode[] = []
  let lastIndex = 0
  let match

  while ((match = codeBlockRegex.exec(content)) !== null) {
    // Add text before code block
    if (match.index > lastIndex) {
      parts.push(
        <span key={`text-${lastIndex}`}>
          {renderInlineContent(content.slice(lastIndex, match.index))}
        </span>
      )
    }

    // Add code block
    parts.push(
      <pre
        key={`code-${match.index}`}
        className="my-2 overflow-x-auto rounded-lg bg-neutral-800 p-3 text-xs text-neutral-200"
      >
        <code>{match[2]}</code>
      </pre>
    )

    lastIndex = match.index + match[0].length
  }

  // Add remaining text
  if (lastIndex < content.length) {
    parts.push(
      <span key={`text-${lastIndex}`}>
        {renderInlineContent(content.slice(lastIndex))}
      </span>
    )
  }

  return parts.length > 0 ? parts : renderInlineContent(content)
}

/**
 * Render inline content (non-code-block text)
 */
function renderInlineContent(content: string): React.ReactNode {
  // Split by lines to handle list items and paragraphs
  const lines = content.split('\n')
  const elements: React.ReactNode[] = []

  let inList = false
  let listItems: React.ReactNode[] = []
  let listType: 'ul' | 'ol' = 'ul'

  lines.forEach((line, lineIndex) => {
    const trimmedLine = line.trim()

    // Check for unordered list
    if (trimmedLine.match(/^[-*]\s+/)) {
      if (!inList || listType !== 'ul') {
        if (inList) {
          elements.push(renderList(listItems, listType, lineIndex))
          listItems = []
        }
        inList = true
        listType = 'ul'
      }
      listItems.push(
        <li key={lineIndex} className="ml-4">
          {renderTextFormatting(trimmedLine.replace(/^[-*]\s+/, ''))}
        </li>
      )
      return
    }

    // Check for ordered list
    const orderedMatch = trimmedLine.match(/^(\d+)\.\s+/)
    if (orderedMatch) {
      if (!inList || listType !== 'ol') {
        if (inList) {
          elements.push(renderList(listItems, listType, lineIndex))
          listItems = []
        }
        inList = true
        listType = 'ol'
      }
      listItems.push(
        <li key={lineIndex} className="ml-4">
          {renderTextFormatting(trimmedLine.replace(/^\d+\.\s+/, ''))}
        </li>
      )
      return
    }

    // Not a list item - flush any pending list
    if (inList) {
      elements.push(renderList(listItems, listType, lineIndex))
      listItems = []
      inList = false
    }

    // Empty line = paragraph break
    if (trimmedLine === '') {
      elements.push(<div key={`br-${lineIndex}`} className="h-2" />)
      return
    }

    // Regular text line
    elements.push(
      <div key={lineIndex}>
        {renderTextFormatting(line)}
      </div>
    )
  })

  // Flush any remaining list
  if (inList) {
    elements.push(renderList(listItems, listType, lines.length))
  }

  return elements
}

function renderList(
  items: React.ReactNode[],
  type: 'ul' | 'ol',
  key: number
): React.ReactNode {
  if (type === 'ol') {
    return (
      <ol key={`ol-${key}`} className="my-1 list-decimal space-y-0.5">
        {items}
      </ol>
    )
  }
  return (
    <ul key={`ul-${key}`} className="my-1 list-disc space-y-0.5">
      {items}
    </ul>
  )
}

/**
 * Render text with inline formatting (bold, italic, links, images)
 */
function renderTextFormatting(text: string): React.ReactNode {
  const parts: React.ReactNode[] = []
  let remaining = text
  let keyIndex = 0

  while (remaining.length > 0) {
    // Check for image: ![alt](url)
    const imageMatch = remaining.match(/^!\[([^\]]*)\]\(([^)]+)\)/)
    if (imageMatch) {
      parts.push(
        // eslint-disable-next-line @next/next/no-img-element
        <img
          key={keyIndex++}
          src={imageMatch[2]}
          alt={imageMatch[1]}
          className="my-2 max-h-48 max-w-full rounded-lg object-cover"
          onError={(e) => {
            // Hide broken images
            (e.target as HTMLImageElement).style.display = 'none'
          }}
        />
      )
      remaining = remaining.slice(imageMatch[0].length)
      continue
    }

    // Check for link: [text](url)
    const linkMatch = remaining.match(/^\[([^\]]+)\]\(([^)]+)\)/)
    if (linkMatch) {
      parts.push(
        <a
          key={keyIndex++}
          href={linkMatch[2]}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline hover:no-underline"
        >
          {linkMatch[1]}
        </a>
      )
      remaining = remaining.slice(linkMatch[0].length)
      continue
    }

    // Check for bold: **text**
    const boldMatch = remaining.match(/^\*\*([^*]+)\*\*/)
    if (boldMatch) {
      parts.push(
        <strong key={keyIndex++} className="font-semibold">
          {boldMatch[1]}
        </strong>
      )
      remaining = remaining.slice(boldMatch[0].length)
      continue
    }

    // Check for italic: *text* (single asterisk, not followed by another)
    const italicMatch = remaining.match(/^\*([^*]+)\*/)
    if (italicMatch && !remaining.startsWith('**')) {
      parts.push(
        <em key={keyIndex++}>
          {italicMatch[1]}
        </em>
      )
      remaining = remaining.slice(italicMatch[0].length)
      continue
    }

    // Check for inline code: `code`
    const codeMatch = remaining.match(/^`([^`]+)`/)
    if (codeMatch) {
      parts.push(
        <code
          key={keyIndex++}
          className="rounded bg-muted px-1 py-0.5 text-xs"
        >
          {codeMatch[1]}
        </code>
      )
      remaining = remaining.slice(codeMatch[0].length)
      continue
    }

    // Find next special character or take all remaining
    const nextSpecial = remaining.search(/[*`\[!]/)
    if (nextSpecial === -1) {
      // No more special characters
      parts.push(<span key={keyIndex++}>{remaining}</span>)
      break
    } else if (nextSpecial === 0) {
      // Special char didn't match any pattern, treat as literal
      parts.push(<span key={keyIndex++}>{remaining[0]}</span>)
      remaining = remaining.slice(1)
    } else {
      // Text before next special character
      parts.push(<span key={keyIndex++}>{remaining.slice(0, nextSpecial)}</span>)
      remaining = remaining.slice(nextSpecial)
    }
  }

  return parts
}

export function MessageBubble({
  role,
  content,
  timestamp,
  senderType,
  staffName,
}: MessageBubbleProps) {
  const isUser = role === 'user'
  const isStaff = senderType === 'staff'

  return (
    <div
      className={cn(
        'flex gap-2',
        isUser ? 'flex-row-reverse' : 'flex-row'
      )}
    >
      {/* Avatar */}
      <div
        className={cn(
          'flex h-7 w-7 shrink-0 items-center justify-center rounded-full',
          isUser
            ? 'bg-primary text-primary-foreground'
            : isStaff
            ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
            : 'bg-muted'
        )}
      >
        {isUser ? (
          <User className="h-4 w-4" />
        ) : isStaff ? (
          <UserCheck className="h-4 w-4" />
        ) : (
          <Bot className="h-4 w-4" />
        )}
      </div>

      {/* Message Bubble */}
      <div
        className={cn(
          'max-w-[80%] rounded-xl px-3 py-2 text-sm',
          isUser
            ? 'bg-primary text-primary-foreground'
            : isStaff
            ? 'bg-blue-50 dark:bg-blue-900/20 text-foreground'
            : 'bg-muted text-foreground'
        )}
      >
        {/* Staff name label */}
        {isStaff && staffName && (
          <div className="mb-1 text-[10px] font-medium text-blue-600 dark:text-blue-400">
            {staffName}
          </div>
        )}

        <div className="break-words">
          {renderMarkdown(content)}
        </div>

        {/* Timestamp */}
        {timestamp && (
          <div
            className={cn(
              'mt-1 text-[10px] opacity-60',
              isUser ? 'text-right' : 'text-left'
            )}
          >
            {new Date(timestamp).toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </div>
        )}
      </div>
    </div>
  )
}
